"use strict";

/* Quick view — opens a product panel from any card's eye button.
   The content is cloned from the card's inert <template data-qv-template>,
   so everything in the panel is server-rendered Liquid.

   Adding routes through the ORIGIN CARD's own mechanism, never a new one:
   - home preview card → its return_to form (posts, lands on /collections/all);
   - collection/PDP card → the .gr-quantity__button--plus-clone relay, which
     drives the real stepper and the optimistic cart engine, then the panel
     closes and the customer stays where they are. */
document.addEventListener('DOMContentLoaded', () => {
  const shell = document.querySelector('[data-gr-qv]');
  if (!shell) return;
  const panel = shell.querySelector('.gr-qv__panel');
  const content = shell.querySelector('[data-qv-content]');
  let originCard = null;
  let lastFocus = null;

  /* The origin card's price element is the single source of truth: it is
     already synced by gr-purchase-mode (which rewrites the onetime/sub data
     attributes at runtime from the live plan — the server-rendered template
     may not carry them). Copy text AND datasets, so later global syncs keep
     the panel price honest while it is open. */
  const adoptCardPrices = (root, card) => {
    const source = card.querySelector('[data-card-price]');
    root.querySelectorAll('[data-card-price]').forEach(el => {
      if (!source) return;
      if (source.dataset.onetimePrice) el.dataset.onetimePrice = source.dataset.onetimePrice;
      if (source.dataset.subPrice) el.dataset.subPrice = source.dataset.subPrice;
      if (source.textContent.trim()) el.textContent = source.textContent.trim();
    });
  };

  /* Mirror the card's base state into the cloned panel: picker buttons,
     the full base name in the eyebrow, and every calorie figure. One state
     per card — the panel is a view of it, never a second memory. */
  const syncPanelBase = card => {
    const base = (window.grCurrentBaseOf && window.grCurrentBaseOf(card)) ||
      card.getAttribute('data-base-current') || 'oats';
    content.querySelectorAll('[data-base-option]').forEach(btn => {
      const on = btn.getAttribute('data-base-option') === base;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const name = content.querySelector('[data-qv-base-name]');
    if (name) {
      /* The card carries the translated base names (data-base-*-title). */
      name.textContent = card.getAttribute('data-base-' + base + '-title') || (base === 'barley' ? 'Black barley' : 'Rolled oats');
    }
    const cal = card.getAttribute('data-base-' + base + '-cal');
    if (cal) {
      content.querySelectorAll('[data-base-cal]').forEach(el => { el.textContent = cal; });
    }
    /* Nutrition table: one panel per base is in the clone — show the match. */
    content.querySelectorAll('[data-nfp-base]').forEach(el => {
      el.hidden = el.getAttribute('data-nfp-base') !== base;
    });
  };

  const open = (card, opener) => {
    const tpl = card.querySelector('template[data-qv-template]');
    if (!tpl) return false;
    content.innerHTML = '';
    content.appendChild(tpl.content.cloneNode(true));
    adoptCardPrices(content, card);
    syncPanelBase(card);
    const scroll = content.querySelector('[data-qv-scroll]');
    if (scroll) scroll.scrollTop = 0;
    /* Bottom sheet: stop short of the LIVE header edge (announcement bar
       included while it is on screen), never a hardcoded guess. */
    if (window.matchMedia('(max-width: 749px)').matches) {
      const header = document.querySelector('.shopify-section-group-header-group, header, .gr-header');
      const headerBottom = header ? Math.max(56, header.getBoundingClientRect().bottom) : 56;
      panel.style.maxHeight = Math.round(window.innerHeight - headerBottom - 12) + 'px';
    } else {
      panel.style.maxHeight = '';
    }
    originCard = card;
    window.grQuickViewOriginCard = card;
    lastFocus = opener || null;
    shell.classList.add('is-open');
    shell.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    panel.focus({ preventScroll: true });
    return true;
  };

  /* gr-base-choice.js calls this after routing a panel picker click to the
     origin card, so the clone re-reads prices and base state. */
  window.grQuickViewAdopt = () => {
    if (!originCard) return;
    adoptCardPrices(content, originCard);
    syncPanelBase(originCard);
  };

  const close = () => {
    panel.style.transform = '';
    panel.style.transition = '';
    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    originCard = null;
    window.grQuickViewOriginCard = null;
    if (lastFocus && document.contains(lastFocus)) {
      lastFocus.focus({ preventScroll: true });
    }
    lastFocus = null;
  };

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-qv-open]');
    if (opener) {
      const card = opener.closest('.gr-ft-card, .gr-card-product-new');
      if (card && open(card, opener)) event.preventDefault();
      return;
    }

    if (event.target.closest('[data-qv-close]')) {
      if (shell.classList.contains('is-open')) close();
      return;
    }

    const add = event.target.closest('[data-qv-add]');
    if (add && originCard && !add.disabled && !add.classList.contains('is-added')) {
      add.classList.add('is-added');
      const label = add.querySelector('[data-qv-add-label]');
      if (label) label.textContent = 'Added ✓';

      const relay = originCard.querySelector('.gr-quantity__button--plus-clone');
      const form = originCard.querySelector('form.gr-ft-card__form');
      if (relay) {
        /* Collection/PDP card: fire the plus-clone relay into the stepper.
           The optimistic engine takes it from there; stay on the page. */
        relay.click();
        window.setTimeout(close, 550);
      } else if (form) {
        /* Home card: add AND land on the box builder (owner decision) —
           the form's return_to carries the customer to /collections/all
           with the bottle already in the cart. */
        window.setTimeout(() => form.submit(), 350);
      }
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && shell.classList.contains('is-open')) close();
  });

  /* Deep link: /collections/all?qv=<handle> opens that flavour's popup on
     arrival — how the header tiles, the mobile menu and the retired product
     pages (/products/<handle> redirects here) reach a flavour. The param is
     dropped from the address afterwards so a reload shows the plain page. */
  const deepLink = new URLSearchParams(window.location.search).get('qv');
  if (deepLink) {
    const card = document.querySelector('.gr-card-product-new[data-handle="' + deepLink + '"], .gr-ft-card[data-handle="' + deepLink + '"]');
    if (card) {
      window.setTimeout(() => {
        card.scrollIntoView({ block: 'center' });
        open(card, null);
      }, 150);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('qv');
    window.history.replaceState(null, '', url.pathname + (url.search || '') + url.hash);
  }

  /* Mobile sheet: drag down from the top zone (grip area) to dismiss. */
  let dragStartY = null;
  let dragDelta = 0;
  let dragging = false;

  panel.addEventListener('touchstart', event => {
    const rect = panel.getBoundingClientRect();
    if (event.touches[0].clientY - rect.top <= 64) {
      dragStartY = event.touches[0].clientY;
      dragDelta = 0;
      dragging = true;
      panel.style.transition = 'none';
    }
  }, { passive: true });

  panel.addEventListener('touchmove', event => {
    if (!dragging) return;
    dragDelta = Math.max(0, event.touches[0].clientY - dragStartY);
    panel.style.transform = 'translateY(' + dragDelta + 'px)';
  }, { passive: true });

  panel.addEventListener('touchend', () => {
    if (!dragging) return;
    dragging = false;
    panel.style.transition = '';
    if (dragDelta > 80) {
      close();
    } else {
      panel.style.transform = '';
    }
  });
});
